import React, { useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { PaletteMode } from '@mui/material'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { CLIENT_ID } from 'components/MyTwin/MyTwinFiles'
import getDesignTokens from './theme'
import { UpdateKeyContext, useKeyContext } from 'hooks/use-context-key'

const queryClient = new QueryClient()

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} })

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const { key, updateKey } = useKeyContext()
  const [mode, setMode] = React.useState<PaletteMode>('light')

  // used to determine user's OS setting for dark / light mode
  // TODO: uncomment the below line once dark mode is ready
  // const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const colorMode = React.useMemo(
    () => ({
      // The dark mode switch would invoke this method
      toggleColorMode: () => {
        setMode((prevMode: PaletteMode) => (prevMode === 'light' ? 'dark' : 'light'))
      },
    }),
    []
  )

  // on initial load, we should default mode to be the one in localStorage
  useEffect(() => {
    // first, if there is something in localStorage, we should use it
    // const localMode = localStorage.getItem('mode') as PaletteMode | null
    // if (localMode) {
    //   setMode(localMode)
    //   return
    // }

    // TODO: remove this when dark mode is ready
    setMode('light')

    // TODO: uncomment this when dark mode is ready
    // otherwise, we should rely on the user's OS / system setting
    // if (prefersDarkMode) {
    //   setMode('dark')
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // whenever dark mode switches, we should update it in localStorage
  useEffect(() => {
    localStorage.setItem('jelled_mode', mode)
  }, [mode])

  // Update the theme only if the mode changes
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode])

  return (
    <UpdateKeyContext.Provider value={updateKey}>
      <RecoilRoot key={key}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <ColorModeContext.Provider value={colorMode}>
              <ThemeProvider theme={theme}>
                <GoogleOAuthProvider clientId={CLIENT_ID ?? ''}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>{children}</LocalizationProvider>
                </GoogleOAuthProvider>
              </ThemeProvider>
            </ColorModeContext.Provider>
          </QueryClientProvider>
        </BrowserRouter>
      </RecoilRoot>
    </UpdateKeyContext.Provider>
  )
}

export default AppProvider
